import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, UserPlus, ChatCircleText, ChatTeardropDots, Stack, CheckCircle } from "@phosphor-icons/react";
import { timeAgo, formatDateTime } from "@/components/ProfileWidgets";
import { Button } from "@/components/ui/button";

const TYPE_ICONS = { follow: UserPlus, comment_reply: ChatCircleText, message: ChatTeardropDots, new_volume: Stack };

export default function Notifications() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && !user) { navigate("/login"); return; }
    if (user) {
      api.get("/me/notifications")
        .then(r => setItems(r.data))
        .catch(() => setItems([]))
        .finally(() => setBusy(false));
      api.post("/me/notifications/read-all").catch(() => {});
    }
  }, [user, loading, navigate]);

  async function markAllRead() {
    await api.post("/me/notifications/read-all").catch(() => {});
    setItems(items.map(n => ({ ...n, read: true })));
  }

  function goTo(n) {
    if (n.link) navigate(n.link);
  }

  if (!user) return null;

  return (
    <div className="pt-24 pb-24 max-w-3xl mx-auto px-4" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#E63946]/10 text-[#E63946] grid place-items-center">
            <Bell size={22} weight="fill" />
          </div>
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">Notificações</h1>
            <div className="text-sm text-zinc-500">O teu histórico de atividade</div>
          </div>
        </div>
        {items.some(n => !n.read) && (
          <Button variant="outline" size="sm" onClick={markAllRead} className="border-white/15 text-white hover:bg-white/5" data-testid="mark-all-read-btn">
            <CheckCircle size={16} weight="bold" className="mr-1.5" /> Marcar todas como lidas
          </Button>
        )}
      </div>

      {busy && (
        <div className="py-16 text-center text-zinc-500 font-mono-accent text-xs">A CARREGAR...</div>
      )}

      {!busy && items.length === 0 && (
        <div className="border border-dashed border-white/10 rounded-2xl p-16 text-center" data-testid="notifications-page-empty">
          <Bell size={48} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <div className="font-display font-bold text-xl">Sem notificações</div>
          <div className="text-sm text-zinc-500 mt-1">Quando alguém te seguir, responder ou enviar mensagem, aparece aqui.</div>
        </div>
      )}

      {!busy && items.length > 0 && (
        <div className="border border-white/5 rounded-2xl bg-[#0f0f14] divide-y divide-white/5 overflow-hidden">
          {items.map((n) => {
            const Icon = TYPE_ICONS[n.type] || Bell;
            return (
              <button
                key={n.notification_id}
                onClick={() => goTo(n)}
                className={`w-full flex items-start gap-4 px-5 py-4 text-left transition-colors hover:bg-white/5 ${n.read ? "" : "bg-[#E63946]/5"}`}
                data-testid={`notification-row-${n.notification_id}`}
              >
                <div className="relative flex-shrink-0">
                  {n.actor_picture ? (
                    <img src={resolveImageUrl(n.actor_picture)} alt="" className="w-11 h-11 rounded-full object-cover" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-sm font-display font-bold">
                      {(n.actor_name || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#0f0f14] grid place-items-center text-[#E63946]">
                    <Icon size={13} weight="fill" />
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-200 leading-snug">{n.message}</div>
                  <div className="text-xs text-zinc-500 mt-1">{timeAgo(n.created_at)} · {formatDateTime(n.created_at)}</div>
                </div>
                {!n.read && <span className="w-2.5 h-2.5 rounded-full bg-[#E63946] flex-shrink-0 mt-2" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
